import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchReglasNivel,
    ReglaNivelUsuario
} from '../../../services/reglasNivelUsuariosService';
import AddReglasNivelPage from '../../../components/Reglas_nivel/AddReglasNivelPage';
import EditReglasNivelPage from '../../../components/Reglas_nivel/EditReglasNivelPage';
import DeleteReglasNivelPage from '../../../components/Reglas_nivel/DeleteReglasNivelPage';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission'; 

const sortByPriorityDesc = (a: ReglaNivelUsuario, b: ReglaNivelUsuario) => b.prioridad - a.prioridad;

const ReglasNivelPage: React.FC = () => {
    const [reglas, setReglas] = useState<ReglaNivelUsuario[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRegla, setSelectedRegla] = useState<ReglaNivelUsuario | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth(); // 👈

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('reglanivelusuario', userLevelId);

    const loadReglas = useCallback(async () => {
        if (!perm?.can_read) {
            setReglas([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchReglasNivel();
            const sortedData = [...data].sort(sortByPriorityDesc);
            setReglas(sortedData);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadReglas();
    }, [loadReglas]);

    const totalPages = Math.ceil(reglas.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedReglas = reglas.slice(startIndex, startIndex + itemsPerPage);

    const handleReglaCreated = (newRegla: ReglaNivelUsuario) => {
        setReglas(prev => [...prev, newRegla].sort(sortByPriorityDesc));
    };

    const handleReglaUpdated = (updatedRegla: ReglaNivelUsuario) => {
        setReglas(prev =>
            prev.map(r => (r.id === updatedRegla.id ? updatedRegla : r)).sort(sortByPriorityDesc)
        );
    };

    const handleReglaDeleted = (deletedId: number) => {
        setReglas(prev => prev.filter(r => r.id !== deletedId));
    };

    const handleCreate = () => setShowAddModal(true);
    const handleEdit = (regla: ReglaNivelUsuario) => {
        setSelectedRegla(regla);
        setShowEditModal(true);
    };
    const handleDeleteClick = (regla: ReglaNivelUsuario) => {
        setSelectedRegla(regla);
        setShowDeleteModal(true);
    };

    const formatCurrency = (value: string | null) => {
        if (!value) return '—';
        const num = parseFloat(value);
        if (isNaN(num)) return value;
        return new Intl.NumberFormat('es-BO', { style: 'currency', currency: 'BOB' }).format(num);
    };

    if (perm === null) return <Loading text="Cargando reglas de nivel..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Reglas de Nivel de Usuarios</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/reglas-nivel" className="text-[#c1348b] hover:text-[#8f1f64]">Reglas de Nivel</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        onClick={loadReglas}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center h-[39px] text-sm font-medium transition-colors"
                        title="Refrescar lista"
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            onClick={handleCreate}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center h-[39px] text-sm font-medium transition-colors min-w-[170px]"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Reglas de Nivel
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {showAddModal && (
                    <AddReglasNivelPage
                        onClose={() => setShowAddModal(false)}
                        onCreated={handleReglaCreated}
                    />
                )}
                {showEditModal && selectedRegla && (
                    <EditReglasNivelPage
                        regla={selectedRegla}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedRegla(null);
                        }}
                        onUpdated={handleReglaUpdated}
                    />
                )}
                {showDeleteModal && selectedRegla && (
                    <DeleteReglasNivelPage
                        regla={selectedRegla}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedRegla(null);
                        }}
                        onDeleted={handleReglaDeleted}
                    />
                )}

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nivel de Usuario</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mín. Citas</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Período (días)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duración mín. (días)</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto mín. pagado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prioridad</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={10} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando reglas...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : perm.can_read ? (
                                displayedReglas.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <Notebook className="w-12 h-12 text-gray-400" />
                                                <span className="text-lg font-medium text-gray-600">
                                                    No hay reglas disponibles
                                                </span>
                                                {perm.can_create && (
                                                    <p className="text-gray-500 text-sm">
                                                        Agrega una nueva regla para comenzar
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    displayedReglas.map(regla => (
                                        <tr key={regla.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-900">{regla.nombre}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {regla.nivel_destino_nombre || `ID: ${regla.nivel_destino}`}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{regla.min_citas ?? '—'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{regla.periodo_dias ?? '—'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{regla.min_duracion_total_dias ?? '—'}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{formatCurrency(regla.min_monto_pagado)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{regla.prioridad}</td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {new Date(regla.fecha_creacion).toLocaleDateString('es-ES', {
                                                    day: '2-digit',
                                                    month: '2-digit',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </td>
                                            <td className="px-4 py-4">
                                                <span className={`px-2 inline-flex text-xs font-medium rounded-full ${
                                                    regla.activo
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                }`}>
                                                    {regla.activo ? 'Activo' : 'Inactivo'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex gap-2">
                                                    {perm.can_update && (
                                                        <button
                                                            onClick={() => handleEdit(regla)}
                                                            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                            title="Editar"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(regla)}
                                                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                <tr>
                                    <td colSpan={10} className="px-6 py-12 text-center text-gray-500">
                                        No tienes permiso para ver las reglas de nivel.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {!loading && perm.can_read && reglas.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={reglas.length}
                    />
                )}
            </div>
        </div>
    );
};

export default ReglasNivelPage;
